import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Shield, Users, Building2, Award, BookOpen, TrendingUp, Eye, UserCog, AlertTriangle, LogOut, Trash2, Download, Plus, Edit2, Upload, Search, ArrowRightLeft, MoreVertical, CreditCard, Unlock, Lock, Gift, CheckCircle, Settings } from 'lucide-react';
import SuperAdminJSAManager from '@/components/SuperAdminJSAManager';
import TenantInventory from '@/components/TenantInventory';

const SuperAdminDashboard = ({ user, onLogout }) => {
  const { t } = useTranslation();
  console.log('🚀 SuperAdminDashboard v2.0 - Course Management Feature Loaded!');
  
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Course management state
  const [courses, setCourses] = useState([]);
  const [showCourseDialog, setShowCourseDialog] = useState(false);
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    category: 'Safety',
    content: '',
    duration: 30,
    imageUrl: '',
    videoUrl: '',
    companyId: null,
    trainingType: 'general'
  });
  
  // Video upload state
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  
  // Manual company/user creation state
  const [showCreateCompanyDialog, setShowCreateCompanyDialog] = useState(false);
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    companyCode: '',
    contactEmail: '',
    contactName: '',
    address: '',
    phone: '',
    subscriptionStatus: 'active',
    isFreeAccount: false
  });
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'worker',
    companyId: '',
    craft: ''
  });
  const [crafts, setCrafts] = useState([]);
  
  // Edit User States
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: '',
    isActive: true
  });
  
  // Delete User States
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Transfer Worker States
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [workerToTransfer, setWorkerToTransfer] = useState(null);
  const [workerCertificates, setWorkerCertificates] = useState([]);
  const [loadingCertificates, setLoadingCertificates] = useState(false);
  const [destinationCompanyCode, setDestinationCompanyCode] = useState('');
  const [transferring, setTransferring] = useState(false);
  
  // All Users Management
  const [allUsers, setAllUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userPage, setUserPage] = useState(1);
  const USERS_PER_PAGE = 50;
  
  // Usage Dashboard State
  const [usageData, setUsageData] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('companies'); // 'companies', 'users', 'usage', 'courses', 'parentgc'
  
  // Parent GC Management State
  const [parentGcHierarchy, setParentGcHierarchy] = useState(null);
  const [showAssignParentDialog, setShowAssignParentDialog] = useState(false);
  const [companyToAssign, setCompanyToAssign] = useState(null);
  const [selectedParentGc, setSelectedParentGc] = useState('');

  useEffect(() => {
    loadSuperAdminData();
    loadCourses();
    loadAllUsers();
    loadCrafts();
    loadUsageData();
    loadParentGcHierarchy();
  }, []);

  const loadCrafts = async () => {
    try {
      const response = await axiosInstance.get('/crafts');
      setCrafts(response.data);
    } catch (error) {
      console.error('Failed to load crafts:', error);
    }
  };
  
  const loadUsageData = async () => {
    try {
      setUsageLoading(true);
      const response = await axiosInstance.get('/superadmin/usage/dashboard');
      setUsageData(response.data);
    } catch (error) {
      console.error('Failed to load usage data:', error);
    } finally {
      setUsageLoading(false);
    }
  };
  
  const refreshUsageData = async () => {
    try {
      await loadUsageData();
      toast.success(t('adminToasts.UsageDataRefreshed'));
    } catch (error) {
      toast.error(t('adminToasts.FailedToRefreshUsageData'));
    }
  };
  
  const seedHistoricalData = async () => {
    try {
      const response = await axiosInstance.post('/superadmin/usage/seed-historical');
      toast.success(`${response.data.message}: ${response.data.recordsCreated} records created`);
      await loadUsageData(); // Refresh to show new data
    } catch (error) {
      toast.error(t('adminToasts.FailedToSeedHistoricalData'));
    }
  };
  
  const exportUsageData = async () => {
    try {
      const response = await axiosInstance.get('/superadmin/usage/export');
      
      // Convert to CSV
      const data = response.data.data;
      const headers = ['Company Name', 'Company Code', 'Status', 'Free Account', 'Workers', 'Supervisors', 'Admins', 'Billable Supervisors', 'Billable Admins'];
      const csvRows = [headers.join(',')];
      
      data.forEach(row => {
        csvRows.push([
          `"${row.companyName}"`,
          row.companyCode,
          row.subscriptionStatus,
          row.isFreeAccount,
          row.workers,
          row.supervisors,
          row.admins,
          row.billableSupervisors,
          row.billableAdmins
        ].join(','));
      });
      
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `usage-export-${response.data.month}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('adminToasts.UsageDataExported'));
    } catch (error) {
      toast.error(t('adminToasts.FailedToExportUsageData'));
    }
  };
  
  // Parent GC Management Functions
  const loadParentGcHierarchy = async () => {
    try {
      const response = await axiosInstance.get('/superadmin/parent-gc/hierarchy');
      setParentGcHierarchy(response.data);
    } catch (error) {
      console.error('Failed to load Parent GC hierarchy:', error);
    }
  };
  
  const toggleParentGcStatus = async (companyId) => {
    try {
      const response = await axiosInstance.patch(`/superadmin/company/${companyId}/parent-gc`);
      toast.success(response.data.message);
      loadSuperAdminData();
      loadParentGcHierarchy();
    } catch (error) {
      toast.error(t('adminToasts.FailedToUpdateParentGcStatus'));
    }
  };
  
  const assignParentGc = async () => {
    if (!companyToAssign) return;
    
    try {
      const response = await axiosInstance.patch(`/superadmin/company/${companyToAssign.id}/assign-parent-gc`, {
        parentGcId: selectedParentGc === 'none' ? null : selectedParentGc
      });
      toast.success(response.data.message);
      setShowAssignParentDialog(false);
      setCompanyToAssign(null);
      setSelectedParentGc('');
      loadSuperAdminData();
      loadParentGcHierarchy();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign Parent GC');
    }
  };
  
  const archiveGlobalCourses = async () => {
    if (!window.confirm('Are you sure you want to archive ALL global courses? Companies will need to create their own training. Existing certificates will remain visible.')) {
      return;
    }
    
    try {
      const response = await axiosInstance.post('/superadmin/archive-global-courses');
      toast.success(response.data.message);
      loadCourses(); // Refresh course list
    } catch (error) {
      console.error('Failed to archive courses:', error);
      toast.error(error.response?.data?.detail || 'Failed to archive global courses');
    }
  };
  
  const unarchiveCourse = async (courseId) => {
    try {
      const response = await axiosInstance.post(`/superadmin/unarchive-course/${courseId}`);
      toast.success(t('adminToasts.CourseUnarchivedSuccessfully'));
      loadCourses(); // Refresh course list
    } catch (error) {
      console.error('Failed to unarchive course:', error);
      toast.error(error.response?.data?.detail || 'Failed to unarchive course');
    }
  };
  
  const archiveCourse = async (courseId) => {
    try {
      const response = await axiosInstance.post(`/superadmin/archive-course/${courseId}`);
      toast.success(t('adminToasts.CourseArchivedSuccessfully'));
      loadCourses(); // Refresh course list
    } catch (error) {
      console.error('Failed to archive course:', error);
      toast.error(error.response?.data?.detail || 'Failed to archive course');
    }
  };

  const loadSuperAdminData = async () => {
    try {
      console.log('Loading super admin data...');
      console.log('User role:', user?.role);
      
      const [analyticsRes, companiesRes] = await Promise.all([
        axiosInstance.get('/superadmin/analytics'),
        axiosInstance.get('/superadmin/companies')
      ]);
      
      console.log('Super admin data loaded successfully');
      setAnalytics(analyticsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error('Failed to load super admin data:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.status === 403) {
        toast.error(t('adminToasts.AccessDeniedPleaseEnsureYouAreLoggedInAsSuperAdmin'));
      } else {
        toast.error(t('adminToasts.FailedToLoadSuperAdminData'));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      const response = await axiosInstance.get('/admin/all-courses');
      setCourses(response.data);
    } catch (error) {
      console.error('Failed to load courses:', error);
      toast.error(t('adminToasts.FailedToLoadCourses'));
    }
  };
  
  const loadAllUsers = async () => {
    try {
      const response = await axiosInstance.get('/superadmin/all-users');
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to load all users:', error);
      toast.error(t('adminToasts.FailedToLoadUsers'));
    }
  };

  const viewCompanyDetails = async (company) => {
    try {
      const response = await axiosInstance.get(`/superadmin/company/${company.id}`);
      setCompanyDetails(response.data);
      setSelectedCompany(company);
      setShowCompanyDialog(true);
    } catch (error) {
      toast.error(t('adminToasts.FailedToLoadCompanyDetails'));
    }
  };

  const impersonateCompany = async (companyId) => {
    try {
      console.log('Impersonating company:', companyId);
      console.log('Current token exists:', !!localStorage.getItem('token'));
      
      const response = await axiosInstance.post(`/superadmin/impersonate/${companyId}`);
      
      console.log('Impersonation successful:', response.data);
      
      // Store the impersonation token
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success(response.data.message);
      
      // Navigate based on user role (same logic as login)
      const user = response.data.user;
      if (user.role === 'gc_admin' && user.isParentGc) {
        window.location.href = '/parent-gc/dashboard';
      } else if (user.role === 'gc_admin') {
        window.location.href = '/gc/dashboard';
      } else {
        window.location.href = '/admin';
      }
    } catch (error) {
      console.error('Impersonation error:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMsg = error.response?.data?.detail || 'Failed to impersonate company';
      toast.error(errorMsg);
      
      // If auth error, might need to re-login
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error(t('adminToasts.SessionMayHaveExpiredPleaseRefreshAndTryAgain'));
      }
    }
  };

  const downloadAllCertificates = async () => {
    try {
      const response = await axiosInstance.get('/superadmin/download-all-certificates');
      const certificates = response.data;
      
      if (certificates.length === 0) {
        toast.info(t('adminToasts.NoCertificatesAvailableToDownload'));
        return;
      }
      
      // Convert to CSV
      const headers = ['Company', 'Employee Name', 'Email', 'Course', 'Issued Date', 'Certificate ID'];
      const csvContent = [
        headers.join(','),
        ...certificates.map(cert => [
          cert.companyName,
          cert.userName,
          cert.userEmail,
          cert.courseTitle,
          new Date(cert.issuedAt).toLocaleDateString(),
          cert.id
        ].join(','))
      ].join('\n');
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `all_certificates_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${certificates.length} certificates from all companies`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error(t('adminToasts.FailedToDownloadCertificates'));
    }
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/mkv'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('adminToasts.InvalidFileTypePleaseUploadMpAviMovWmvOrMkvFilesOnly'));
      return;
    }
    
    // Validate file size (500MB max)
    const maxSize = 500 * 1024 * 1024; // 500MB
    if (file.size > maxSize) {
      toast.error(t('adminToasts.FileTooLargeMaximumSizeIsMb'));
      return;
    }
    
    try {
      setUploadingVideo(true);
      setSelectedVideoFile(file);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axiosInstance.post('/upload/video', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Set the uploaded video URL
      setCourseForm({ ...courseForm, videoUrl: response.data.videoUrl });
      toast.success(`Video uploaded successfully: ${file.name}`);
      
    } catch (error) {
      console.error('Video upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload video');
      setSelectedVideoFile(null);
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleCreateCourse = async () => {
    try {
      // Validate required fields
      if (!courseForm.title || !courseForm.description || !courseForm.content) {
        toast.error(t('adminToasts.PleaseFillInAllRequiredFields'));
        return;
      }

      const payload = {
        ...courseForm,
        companyId: courseForm.companyId === 'global' ? null : courseForm.companyId,
        trainingType: courseForm.trainingType || 'general'
      };

      await axiosInstance.post('/courses', payload);
      toast.success(`Course "${courseForm.title}" created successfully`);
      
      // Reset form and reload
      setCourseForm({
        title: '',
        description: '',
        category: 'Safety',
        content: '',
        duration: 30,
        imageUrl: '',
        videoUrl: '',
        companyId: null,
        trainingType: 'general'
      });
      setSelectedVideoFile(null);
      setShowCourseDialog(false);
      await loadCourses();
    } catch (error) {
      console.error('Failed to create course:', error);
      toast.error(error.response?.data?.detail || 'Failed to create course');
    }
  };

  // Deleting a company happens in two steps: a dry run that reports exactly what would go, then
  // the real purge. A half-delete would leave orphaned safety records nobody can verify again.
  const loadDeletePreview = async (company) => {
    setDeletePreview(null);
    setDeletePreviewLoading(true);
    try {
      const { data } = await axiosInstance.delete(`/superadmin/company/${company.id}`);
      setDeletePreview(data);
    } catch (error) {
      console.error('Delete preview error:', error);
      toast.error(error.response?.data?.detail || 'Could not read what would be deleted');
    } finally {
      setDeletePreviewLoading(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!companyToDelete) return;

    setDeleting(true);
    try {
      const response = await axiosInstance.delete(
        `/superadmin/company/${companyToDelete.id}?dry_run=false`
      );
      if (response.data?.refused) {
        toast.error(response.data.refused);
        return;
      }
      toast.success(response.data.message || `Company "${companyToDelete.name}" deleted`);
      
      // Reload data
      await loadSuperAdminData();
      
      // Close dialog
      setShowDeleteDialog(false);
      setCompanyToDelete(null);
      setDeletePreview(null);
    } catch (error) {
      console.error('Delete company error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    } finally {
      setDeleting(false);
    }
  };

  const toggleFreeAccount = async (company) => {
    try {
      const response = await axiosInstance.patch(`/superadmin/company/${company.id}/free-account`);
      toast.success(response.data.message);
      
      // Reload data to reflect changes
      await loadSuperAdminData();
    } catch (error) {
      console.error('Toggle free account error:', error);
      toast.error(error.response?.data?.detail || 'Failed to toggle free account status');
    }
  };

  const toggleGcAccess = async (company) => {
    try {
      const response = await axiosInstance.patch(`/superadmin/company/${company.id}/gc-access`);
      toast.success(response.data.message);
      
      // Reload data to reflect changes
      await loadSuperAdminData();
    } catch (error) {
      console.error('Toggle GC access error:', error);
      toast.error(error.response?.data?.detail || 'Failed to toggle GC access');
    }
  };

  const activateSubscription = async (company) => {
    if (!confirm(`Activate full subscription for "${company.name}"? This will remove trial restrictions.`)) {
      return;
    }
    try {
      const response = await axiosInstance.patch(`/superadmin/company/${company.id}/activate-subscription`);
      toast.success(response.data.message);
      
      // Reload data to reflect changes
      await loadSuperAdminData();
    } catch (error) {
      console.error('Activate subscription error:', error);
      toast.error(error.response?.data?.detail || 'Failed to activate subscription');
    }
  };
  
  // Edit User Functions
  const openEditUserDialog = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditUserForm({
      name: userToEdit.name || '',
      email: userToEdit.email || '',
      password: '', // Always start empty for security
      phone: userToEdit.phone || '',
      role: userToEdit.role || '',
      isActive: userToEdit.isActive !== undefined ? userToEdit.isActive : true
    });
    setShowEditUserDialog(true);
  };
  
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    try {
      // Only send fields that have values
      const updateData = {};
      if (editUserForm.name) updateData.name = editUserForm.name;
      if (editUserForm.email) updateData.email = editUserForm.email;
      if (editUserForm.password) updateData.password = editUserForm.password;
      if (editUserForm.phone) updateData.phone = editUserForm.phone;
      if (editUserForm.role) updateData.role = editUserForm.role;
      updateData.isActive = editUserForm.isActive;
      
      const response = await axiosInstance.patch(`/admin/users/${editingUser.id}`, updateData);
      
      toast.success(t('adminToasts.UserUpdatedSuccessfully'));
      setShowEditUserDialog(false);
      setEditingUser(null);
      
      // Reload all users list
      loadAllUsers();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };
  
  // Delete User Function
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      const response = await axiosInstance.delete(`/admin/users/${userToDelete.id}`);
      toast.success(response.data.message || `User ${userToDelete.name} deleted successfully`);
      
      // Close dialog
      setShowDeleteUserDialog(false);
      setUserToDelete(null);
      
      // Reload all users list
      loadAllUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const loadWorkerCertificates = async (workerId) => {
    setLoadingCertificates(true);
    try {
      const response = await axiosInstance.get(`/superadmin/user-certificates/${workerId}`);
      setWorkerCertificates(response.data.certificates || []);
    } catch (error) {
      console.error('Error loading worker certificates:', error);
      setWorkerCertificates([]);
    } finally {
      setLoadingCertificates(false);
    }
  };

  const handleTransferWorker = async () => {
    if (!workerToTransfer || !destinationCompanyCode.trim()) {
      toast.error(t('adminToasts.PleaseEnterADestinationCompanyCode'));
      return;
    }

    setTransferring(true);
    try {
      await axiosInstance.post('/companies/transfer-worker', {
        workerId: workerToTransfer.id,
        destinationCompanyCode: destinationCompanyCode.trim().toUpperCase()
      });

      toast.success(`Worker ${workerToTransfer.name} successfully transferred`);
      
      // Close dialog and reset
      setShowTransferDialog(false);
      setWorkerToTransfer(null);
      setDestinationCompanyCode('');
      
      // Reload users list
      loadAllUsers();
    } catch (error) {
      console.error('Error transferring worker:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to transfer worker';
      toast.error(`Transfer failed: ${errorMsg}`);
    } finally {
      setTransferring(false);
    }
  };

  const handleManualCreateCompany = async () => {
    try {
      // Validate required fields
      if (!companyForm.name || !companyForm.companyCode || !companyForm.contactEmail || !companyForm.contactName) {
        toast.error(t('adminToasts.PleaseFillInAllRequiredFields'));
        return;
      }

      const response = await axiosInstance.post('/superadmin/company/manual-create', companyForm);
      toast.success(response.data.message);
      
      // Reset form and reload
      setCompanyForm({
        name: '',
        companyCode: '',
        contactEmail: '',
        contactName: '',
        address: '',
        phone: '',
        subscriptionStatus: 'active',
        isFreeAccount: false
      });
      setShowCreateCompanyDialog(false);
      await loadSuperAdminData();
    } catch (error) {
      console.error('Failed to create company:', error);
      toast.error(error.response?.data?.detail || 'Failed to create company');
    }
  };

  const handleManualCreateUser = async () => {
    try {
      // Validate required fields
      if (!userForm.email || !userForm.password || !userForm.name || !userForm.companyId) {
        toast.error(t('adminToasts.PleaseFillInAllRequiredFields'));
        return;
      }
      
      // Validate craft for workers
      if (userForm.role === 'worker' && !userForm.craft) {
        toast.error(t('adminToasts.PleaseSelectACraftForTheWorker'));
        return;
      }

      const response = await axiosInstance.post('/superadmin/user/manual-create', userForm);
      toast.success(response.data.message);
      
      // Reset form and reload
      setUserForm({
        email: '',
        password: '',
        name: '',
        role: 'worker',
        companyId: '',
        craft: ''
      });
      setShowCreateUserDialog(false);
      await loadSuperAdminData();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    company.companyCode.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading super admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-yellow-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">{t('adminUI.SafetymindAiSuperAdmin')}</h1>
                <p className="text-slate-600">{t('adminUI.SystemWideManagementAndSupport')}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-slate-600">Welcome, {user.name}</span>
              <div className="flex gap-2">
                <Button
                  onClick={() => navigate('/superadmin/modules')}
                  size="sm"
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Modules
                </Button>
                <Button
                  onClick={() => navigate('/superadmin/ai-models')}
                  size="sm"
                  variant="outline"
                  data-testid="superadmin-ai-models-btn"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  AI Models
                </Button>
                <Button
                  onClick={() => navigate('/superadmin/historical-review')}
                  size="sm"
                  variant="outline"
                  data-testid="superadmin-historical-review-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Historical Review
                </Button>
                <Button
                  onClick={() => setShowCreateCompanyDialog(true)}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Add Company
                </Button>
                <Button
                  onClick={() => setShowCreateUserDialog(true)}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Add Learner
                </Button>
                <Button
                  onClick={downloadAllCertificates}
                  variant="outline"
                  size="sm"
                  className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download All Certificates
                </Button>
                <Button
                  onClick={onLogout}
                  variant="outline"
                  size="sm"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center">
                  <Building2 className="w-4 h-4 mr-2" />
                  Total Companies
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">{analytics.totalCompanies}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  Total Users
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-800">{analytics.totalUsers}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Total Courses
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-800">{analytics.totalCourses}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Completions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">{analytics.totalCompletions}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center">
                  <Award className="w-4 h-4 mr-2" />
                  Certificates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600">{analytics.totalCertificates}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-6 bg-white rounded-lg p-1 shadow-sm border">
          <button
            onClick={() => setActiveTab('companies')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'companies'
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Companies
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'users'
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Users
          </button>
          <button
            onClick={() => setActiveTab('usage')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'usage'
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <CreditCard className="w-4 h-4 inline mr-2" />
            Usage
          </button>
          <button
            onClick={() => setActiveTab('courses')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'courses'
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <BookOpen className="w-4 h-4 inline mr-2" />
            Courses
          </button>
          <button
            onClick={() => setActiveTab('parentgc')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'parentgc'
                ? 'bg-purple-100 text-purple-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Parent GC
          </button>
          <button
            onClick={() => setActiveTab('inventory')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'inventory'
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="superadmin-tab-inventory"
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Inventory
          </button>
        </div>

        {activeTab === 'inventory' && <TenantInventory />}

        {/* USAGE DASHBOARD TAB */}
        {activeTab === 'usage' && (
          <div className="space-y-6">
            {/* Usage Summary Cards */}
            {usageData && (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">{t('adminUI.TotalCompanies')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-blue-600">{usageData.summary.totalCompanies}</div>
                    <p className="text-xs text-gray-500 mt-1">{t('adminUI.ActiveOrganizations')}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Workers (Free)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-600">{usageData.summary.totalWorkersLoggedIn}</div>
                    <p className="text-xs text-gray-500 mt-1">{t('adminUI.MaxMonthlyLogins')}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Admins (Paid)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-purple-600">{usageData.summary.totalBillableAdmins}</div>
                    <p className="text-xs text-gray-500 mt-1">{t('adminUI.MaxMonthlyLogins')}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Supervisors (Paid)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-indigo-600">{usageData.summary.totalBillableSupervisors}</div>
                    <p className="text-xs text-gray-500 mt-1">{t('adminUI.MaxMonthlyLogins')}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">{t('adminUI.CurrentPeriod')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-orange-600">{usageData.currentMonth}</div>
                    <p className="text-xs text-gray-500 mt-1">{t('adminUI.CalendarMonth')}</p>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Login Stats */}
            {usageData && usageData.loginStats && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">{t('adminUI.LoginActivityThisMonth')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{usageData.loginStats.totalAttempts}</div>
                      <p className="text-xs text-gray-500">{t('adminUI.TotalAttempts')}</p>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{usageData.loginStats.successfulLogins}</div>
                      <p className="text-xs text-gray-500">{t('adminUI.Successful')}</p>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">{usageData.loginStats.failedLogins}</div>
                      <p className="text-xs text-gray-500">{t('adminUI.Failed')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Usage Actions & Table */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t('adminUI.CompanyUsageMaxMonthlyLogins')}</CardTitle>
                    <CardDescription>{t('adminUI.AdminsSupervisorsAreBilledSeparatelyWorkersAreAlwaysFree')}</CardDescription>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button onClick={seedHistoricalData} variant="outline" size="sm" className="text-purple-600 border-purple-300 hover:bg-purple-50">
                      <Users className="w-4 h-4 mr-2" />
                      Seed Users
                    </Button>
                    <Button onClick={refreshUsageData} variant="outline" size="sm">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                    <Button onClick={exportUsageData} size="sm" className="bg-green-600 hover:bg-green-700">
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {usageLoading ? (
                  <div className="text-center py-8 text-gray-500">Loading usage data...</div>
                ) : usageData ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('adminUI.Company')}</TableHead>
                        <TableHead>{t('adminUI.Status')}</TableHead>
                        <TableHead className="text-right">Workers (Free)</TableHead>
                        <TableHead className="text-right">{t('adminUI.Admins')}</TableHead>
                        <TableHead className="text-right">{t('adminUI.Supervisors')}</TableHead>
                        <TableHead className="text-right">{t('adminUI.BillableAdmins')}</TableHead>
                        <TableHead className="text-right">{t('adminUI.BillableSupervisors')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {usageData.companies.map((company) => (
                        <TableRow key={company.companyId}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{company.companyName}</div>
                              <div className="text-xs text-gray-500">{company.companyCode || 'No code'}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Badge 
                                variant="outline"
                                className={
                                  company.subscriptionStatus === 'active' ? 'bg-green-50 text-green-700 border-green-200' :
                                  company.subscriptionStatus === 'trial' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                                  'bg-gray-50 text-gray-600'
                                }
                              >
                                {company.subscriptionStatus}
                              </Badge>
                              {company.isFreeAccount && (
                                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Free</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono text-gray-500">{company.workers}</TableCell>
                          <TableCell className="text-right font-mono text-purple-600">{company.admins}</TableCell>
                          <TableCell className="text-right font-mono text-indigo-600">{company.supervisors}</TableCell>
                          <TableCell className="text-right">
                            <span className={`font-mono font-bold ${company.billableAdmins > 0 ? 'text-purple-600' : 'text-gray-400'}`}>
                              {company.billableAdmins}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={`font-mono font-bold ${company.billableSupervisors > 0 ? 'text-indigo-600' : 'text-gray-400'}`}>
                              {company.billableSupervisors}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-gray-500">No usage data available. Login activity will appear here once users start logging in.</div>
                )}
                
                {/* Total Row */}
                {usageData && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex justify-between items-center">
                      <div className="text-sm text-gray-600">
                        Total: {usageData.companies.length} companies
                      </div>
                      <div className="flex gap-6">
                        <div className="text-right">
                          <p className="text-xs text-gray-500">{t('adminUI.BillableAdmins')}</p>
                          <p className="text-lg font-bold text-purple-600">{usageData.summary.totalBillableAdmins}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">{t('adminUI.BillableSupervisors')}</p>
                          <p className="text-lg font-bold text-indigo-600">{usageData.summary.totalBillableSupervisors}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* COMPANIES TAB */}
        {activeTab === 'companies' && (
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t('adminUI.CompaniesManagement')}</CardTitle>
                <CardDescription>{t('adminUI.ViewAndManageAllRegisteredCompanies')}</CardDescription>
              </div>
              <div className="flex items-center space-x-3">
                <Input
                  placeholder="Search companies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-64"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adminUI.Company')}</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>{t('adminUI.Employees')}</TableHead>
                  <TableHead>{t('adminUI.Status')}</TableHead>
                  <TableHead>{t('adminUI.PaidSeats')}</TableHead>
                  <TableHead>{t('adminUI.GcAccess')}</TableHead>
                  <TableHead>{t('adminUI.Created')}</TableHead>
                  <TableHead>{t('adminUI.Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCompanies.map((company) => (
                  <TableRow key={company.id}>
                    <TableCell>
                      <div>
                        <div className="font-semibold">{company.name}</div>
                        <div className="text-sm text-slate-500">{company.contactEmail || 'No email'}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">
                        {company.companyCode}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {company.companyType === 'gc' ? (
                        <Badge className="bg-blue-500 text-white">GC</Badge>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>{company.activeEmployees} active</div>
                        <div className="text-slate-500">{company.totalEmployees} total</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={company.subscriptionStatus === 'active' ? 'default' : 'secondary'}
                        className={
                          company.subscriptionStatus === 'active' ? 'bg-green-100 text-green-800 hover:bg-green-100' :
                          company.subscriptionStatus === 'trial' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100' :
                          'bg-gray-100 text-gray-600'
                        }
                      >
                        {company.subscriptionStatus === 'active' ? '● Active' : 
                         company.subscriptionStatus === 'trial' ? '○ Trial' : 
                         company.subscriptionStatus || 'Unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {company.isFreeAccount ? (
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                          Free
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-600">
                          Paid
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {company.companyType === 'gc' || company.gcTileUnlocked ? (
                        <Badge className="bg-indigo-500 text-white">{t('adminUI.Unlocked')}</Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-500">{t('adminUI.Locked')}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {company.createdAt ? new Date(company.createdAt).toLocaleDateString() : 'Unknown'}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => viewCompanyDetails(company)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => impersonateCompany(company.id)}>
                            <UserCog className="w-4 h-4 mr-2" />
                            Help / Impersonate
                          </DropdownMenuItem>
                          
                          <DropdownMenuSeparator />
                          
                          {/* Subscription Actions */}
                          {company.subscriptionStatus === 'trial' && (
                            <DropdownMenuItem onClick={() => activateSubscription(company)} className="text-green-600">
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Activate Subscription
                            </DropdownMenuItem>
                          )}
                          
                          {/* Billing Toggle */}
                          <DropdownMenuItem onClick={() => toggleFreeAccount(company)}>
                            {company.isFreeAccount ? (
                              <>
                                <CreditCard className="w-4 h-4 mr-2" />
                                Enable Billing
                              </>
                            ) : (
                              <>
                                <Gift className="w-4 h-4 mr-2" />
                                Make Free Account
                              </>
                            )}
                          </DropdownMenuItem>
                          
                          {/* GC Access Toggle - only for non-GC companies */}
                          {company.companyType !== 'gc' && (
                            <DropdownMenuItem onClick={() => toggleGcAccess(company)}>
                              {company.gcTileUnlocked ? (
                                <>
                                  <Lock className="w-4 h-4 mr-2" />
                                  Lock GC Access
                                </>
                              ) : (
                                <>
                                  <Unlock className="w-4 h-4 mr-2" />
                                  Unlock GC Access
                                </>
                              )}
                            </DropdownMenuItem>
                          )}
                          
                          <DropdownMenuSeparator />
                          
                          {/* Danger Zone */}
                          <DropdownMenuItem 
                            onClick={() => {
                              setCompanyToDelete(company);
                              setShowDeleteDialog(true);
                              loadDeletePreview(company);
                            }}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Company
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        )}

        {/* USERS TAB */}
        {activeTab === 'users' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t('adminUI.AllPlatformUsers')}</CardTitle>
                <CardDescription>{t('adminUI.ManageUsersAcrossAllCompanies')}</CardDescription>
              </div>
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email, company, or role..."
                  value={userSearchQuery}
                  onChange={(e) => { setUserSearchQuery(e.target.value); setUserPage(1); }}
                  className="w-full pl-9"
                />
                {userSearchQuery && (
                  <button
                    onClick={() => { setUserSearchQuery(''); setUserPage(1); }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>{t('adminUI.Email')}</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>{t('adminUI.Company')}</TableHead>
                  <TableHead>{t('adminUI.Status')}</TableHead>
                  <TableHead>{t('adminUI.Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allUsers
                  .filter(u => {
                    if (!userSearchQuery.trim()) return true;
                    const query = userSearchQuery.toLowerCase();
                    return (
                      u.name?.toLowerCase().includes(query) ||
                      u.email?.toLowerCase().includes(query) ||
                      u.role?.toLowerCase().includes(query) ||
                      u.companyName?.toLowerCase().includes(query)
                    );
                  })
                  .slice((userPage - 1) * USERS_PER_PAGE, userPage * USERS_PER_PAGE)
                  .map((usr) => (
                    <TableRow key={usr.id}>
                      <TableCell className="font-medium">{usr.name}</TableCell>
                      <TableCell>{usr.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{usr.role}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{usr.companyName || 'N/A'}</TableCell>
                      <TableCell>
                        <Badge variant={usr.isActive ? 'default' : 'secondary'}>
                          {usr.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditUserDialog(usr)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              setUserToDelete(usr);
                              setShowDeleteUserDialog(true);
                            }}
                          >
                            Delete
                          </Button>
                          {(usr.role === 'worker' || usr.role === 'supervisor') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                setWorkerToTransfer(usr);
                                setShowTransferDialog(true);
                                await loadWorkerCertificates(usr.id);
                              }}
                            >
                              <ArrowRightLeft className="w-3 h-3 mr-1" />
                              Transfer
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            {(() => {
              const filteredLen = allUsers.filter(u => {
                if (!userSearchQuery.trim()) return true;
                const query = userSearchQuery.toLowerCase();
                return (
                  u.name?.toLowerCase().includes(query) ||
                  u.email?.toLowerCase().includes(query) ||
                  u.role?.toLowerCase().includes(query) ||
                  u.companyName?.toLowerCase().includes(query)
                );
              }).length;
              const totalPages = Math.max(1, Math.ceil(filteredLen / USERS_PER_PAGE));
              const safePage = Math.min(userPage, totalPages);
              const startIdx = (safePage - 1) * USERS_PER_PAGE + 1;
              const endIdx = Math.min(safePage * USERS_PER_PAGE, filteredLen);
              return filteredLen > USERS_PER_PAGE ? (
                <div className="flex items-center justify-between mt-4" data-testid="superadmin-users-pagination">
                  <p className="text-sm text-slate-500">
                    Showing {startIdx}-{endIdx} of {filteredLen} users
                    {userSearchQuery && ` matching "${userSearchQuery}"`}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUserPage(p => Math.max(1, p - 1))}
                      disabled={safePage === 1}
                      data-testid="superadmin-users-prev"
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-slate-600">
                      Page {safePage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUserPage(p => Math.min(totalPages, p + 1))}
                      disabled={safePage >= totalPages}
                      data-testid="superadmin-users-next"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500 mt-4">
                  Showing {filteredLen} user{filteredLen === 1 ? '' : 's'}
                  {userSearchQuery && ` matching "${userSearchQuery}"`}
                </p>
              );
            })()}
          </CardContent>
        </Card>
        )}

        {/* COURSES TAB */}
        {activeTab === 'courses' && (
        <>
        {/* JSA Module Management */}
        <SuperAdminJSAManager />

        {/* Course Management */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t('adminUI.CourseManagement')}</CardTitle>
                <CardDescription>{t('adminUI.CreateAndManageCoursesForAllCompanies')}</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  onClick={archiveGlobalCourses}
                  variant="outline"
                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Archive Global Courses
                </Button>
                <Button onClick={() => setShowCourseDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Course
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adminUI.CourseTitle')}</TableHead>
                  <TableHead>{t('adminUI.Category')}</TableHead>
                  <TableHead>{t('adminUI.Duration')}</TableHead>
                  <TableHead>{t('adminUI.Assignment')}</TableHead>
                  <TableHead>{t('adminUI.TrainingType')}</TableHead>
                  <TableHead>{t('adminUI.Status')}</TableHead>
                  <TableHead>{t('adminUI.Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {courses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-500">
                      No courses available. Create your first course.
                    </TableCell>
                  </TableRow>
                ) : (
                  courses.map((course) => {
                    const assignedCompany = companies.find(c => c.id === course.companyId);
                    return (
                      <TableRow key={course.id} className={course.isArchived ? 'opacity-60 bg-gray-50' : ''}>
                        <TableCell>
                          <div>
                            <div className="font-semibold">{course.title}</div>
                            <div className="text-sm text-slate-500">{course.description?.substring(0, 60) || ''}...</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{course.category}</Badge>
                        </TableCell>
                        <TableCell>{course.duration} min</TableCell>
                        <TableCell>
                          {course.companyId ? (
                            <Badge className="bg-blue-100 text-blue-700">
                              {assignedCompany?.name || 'Specific Company'}
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700">{t('adminUI.Global')}</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-gray-100 text-gray-700">
                            {course.trainingType === 'company_private' ? 'Company Private' :
                             course.trainingType === 'company_transferable' ? 'Transferable' :
                             course.trainingType === 'osha_standard' ? 'OSHA Standard' :
                             course.trainingType === 'industry_standard' ? 'Industry Standard' :
                             'General'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {course.isArchived ? (
                            <Badge className="bg-red-100 text-red-700">{t('adminUI.Archived')}</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700">{t('adminUI.Active')}</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {course.isArchived ? (
                            <Button
                              onClick={() => unarchiveCourse(course.id)}
                              size="sm"
                              variant="outline"
                              className="text-green-600 border-green-300 hover:bg-green-50"
                            >
                              <Unlock className="w-4 h-4 mr-1" />
                              Unarchive
                            </Button>
                          ) : (
                            <Button
                              onClick={() => archiveCourse(course.id)}
                              size="sm"
                              variant="outline"
                              className="text-orange-600 border-orange-300 hover:bg-orange-50"
                            >
                              <Lock className="w-4 h-4 mr-1" />
                              Archive
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        </>
        )}

        {/* PARENT GC MANAGEMENT TAB */}
        {activeTab === 'parentgc' && (
          <div className="space-y-6">
            {/* Parent GC Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">{t('adminUI.ParentGcCompanies')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-purple-600">
                    {parentGcHierarchy?.totalParentGcs || 0}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t('adminUI.EnterprisePortfolios')}</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">{t('adminUI.AssignedChildGcs')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-600">
                    {parentGcHierarchy?.hierarchy?.reduce((acc, h) => acc + h.childCount, 0) || 0}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t('adminUI.GcsUnderParentGcManagement')}</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">{t('adminUI.UnassignedGcs')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-orange-600">
                    {parentGcHierarchy?.totalUnassignedGcs || 0}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t('adminUI.GcsWithoutParentAssignment')}</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Parent GC Hierarchy */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t('adminUI.ParentGcHierarchy')}</CardTitle>
                    <CardDescription>{t('adminUI.EnterprisePortfolioStructure')}</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={loadParentGcHierarchy}>
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {parentGcHierarchy?.hierarchy?.length > 0 ? (
                  <div className="space-y-6">
                    {parentGcHierarchy.hierarchy.map((parent) => (
                      <div key={parent.parentGc.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-lg">{parent.parentGc.name}</p>
                              <p className="text-sm text-gray-500">
                                {parent.childCount} child GCs • {parent.totalProjects} projects
                              </p>
                            </div>
                          </div>
                          <Badge className="bg-purple-100 text-purple-700">{t('adminUI.ParentGc')}</Badge>
                        </div>
                        
                        {parent.children.length > 0 ? (
                          <div className="ml-8 border-l-2 border-purple-200 pl-4 space-y-2">
                            {parent.children.map((child) => (
                              <div key={child.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4 text-gray-400" />
                                  <span className="font-medium">{child.name}</span>
                                  <Badge variant="outline" className="text-xs">{child.companyCode}</Badge>
                                </div>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => {
                                    setCompanyToAssign(child);
                                    setSelectedParentGc('none');
                                    setShowAssignParentDialog(true);
                                  }}
                                >
                                  <ArrowRightLeft className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="ml-8 text-sm text-gray-500 italic">{t('adminUI.NoChildGcsAssignedYet')}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No Parent GC companies configured yet
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Unassigned GCs */}
            {parentGcHierarchy?.unassignedGcs?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('adminUI.UnassignedGcCompanies')}</CardTitle>
                  <CardDescription>{t('adminUI.GcCompaniesThatAreNotAssignedToAnyParentGc')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('adminUI.Company')}</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead className="text-right">{t('adminUI.Actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parentGcHierarchy.unassignedGcs.map((gc) => (
                        <TableRow key={gc.id}>
                          <TableCell className="font-medium">{gc.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{gc.companyCode}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  setCompanyToAssign(gc);
                                  setSelectedParentGc('');
                                  setShowAssignParentDialog(true);
                                }}
                              >
                                <ArrowRightLeft className="w-4 h-4 mr-1" />
                                Assign Parent
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-purple-600 border-purple-300 hover:bg-purple-50"
                                onClick={() => toggleParentGcStatus(gc.id)}
                              >
                                <Building2 className="w-4 h-4 mr-1" />
                                Make Parent GC
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
            
            {/* All GC Companies with Parent GC Options */}
            <Card>
              <CardHeader>
                <CardTitle>{t('adminUI.AllGcCompanies')}</CardTitle>
                <CardDescription>{t('adminUI.ManageParentGcStatusForAllGcTypeCompanies')}</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('adminUI.Company')}</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>{t('adminUI.Status')}</TableHead>
                      <TableHead>{t('adminUI.ParentGc')}</TableHead>
                      <TableHead className="text-right">{t('adminUI.Actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companies.filter(c => c.companyType === 'gc').map((company) => (
                      <TableRow key={company.id}>
                        <TableCell className="font-medium">{company.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{company.companyCode}</Badge>
                        </TableCell>
                        <TableCell>
                          {company.isParentGc ? (
                            <Badge className="bg-purple-100 text-purple-700">{t('adminUI.ParentGc')}</Badge>
                          ) : company.parentGcId ? (
                            <Badge className="bg-blue-100 text-blue-700">{t('adminUI.ChildGc')}</Badge>
                          ) : (
                            <Badge variant="outline">{t('adminUI.Standalone')}</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {company.parentGcId ? (
                            <span className="text-sm">
                              {parentGcHierarchy?.hierarchy?.find(h => h.parentGc.id === company.parentGcId)?.parentGc.name || 'Unknown'}
                            </span>
                          ) : company.isParentGc ? (
                            <span className="text-sm text-purple-600">{t('adminUI.IsParentGc')}</span>
                          ) : (
                            <span className="text-sm text-gray-400">None</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {!company.isParentGc && (
                                <DropdownMenuItem onClick={() => toggleParentGcStatus(company.id)}>
                                  <Building2 className="w-4 h-4 mr-2 text-purple-600" />
                                  Make Parent GC
                                </DropdownMenuItem>
                              )}
                              {company.isParentGc && (
                                <DropdownMenuItem onClick={() => toggleParentGcStatus(company.id)}>
                                  <Building2 className="w-4 h-4 mr-2 text-gray-600" />
                                  Remove Parent GC Status
                                </DropdownMenuItem>
                              )}
                              {!company.isParentGc && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => {
                                    setCompanyToAssign(company);
                                    setSelectedParentGc(company.parentGcId || '');
                                    setShowAssignParentDialog(true);
                                  }}>
                                    <ArrowRightLeft className="w-4 h-4 mr-2" />
                                    {company.parentGcId ? 'Change Parent GC' : 'Assign to Parent GC'}
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Assign Parent GC Dialog */}
        <Dialog open={showAssignParentDialog} onOpenChange={setShowAssignParentDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('adminUI.AssignParentGc')}</DialogTitle>
              <DialogDescription>
                Assign "{companyToAssign?.name}" to a Parent GC company
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <Label>{t('adminUI.SelectParentGc')}</Label>
              <Select value={selectedParentGc} onValueChange={setSelectedParentGc}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select a Parent GC..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-gray-500">No Parent (Remove assignment)</span>
                  </SelectItem>
                  {parentGcHierarchy?.hierarchy?.map((h) => (
                    <SelectItem key={h.parentGc.id} value={h.parentGc.id}>
                      {h.parentGc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignParentDialog(false)}>
                Cancel
              </Button>
              <Button onClick={assignParentGc}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Save Assignment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Course Dialog */}
        <Dialog open={showCourseDialog} onOpenChange={setShowCourseDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('adminUI.CreateNewCourse')}</DialogTitle>
              <DialogDescription>{t('adminUI.CreateANewCourseAndOptionallyAssignItToASpecificCompany')}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">{t('adminUI.CourseTitle')}</Label>
                <Input
                  id="title"
                  placeholder="e.g., Fall Protection Safety"
                  value={courseForm.title}
                  onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{t('adminUI.Description')}</Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of the course"
                  value={courseForm.description}
                  onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">{t('adminUI.Category')}</Label>
                  <Input
                    id="category"
                    placeholder="e.g., Safety"
                    value={courseForm.category}
                    onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="duration">Duration (minutes)</Label>
                  <Input
                    id="duration"
                    type="number"
                    placeholder="30"
                    value={courseForm.duration}
                    onChange={(e) => setCourseForm({ ...courseForm, duration: parseInt(e.target.value) || 30 })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">{t('adminUI.CourseContent')}</Label>
                <Textarea
                  id="content"
                  placeholder="Detailed course content and learning materials"
                  value={courseForm.content}
                  onChange={(e) => setCourseForm({ ...courseForm, content: e.target.value })}
                  rows={5}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="imageUrl">Image URL (optional)</Label>
                <Input
                  id="imageUrl"
                  placeholder="https://example.com/course-image.jpg"
                  value={courseForm.imageUrl}
                  onChange={(e) => setCourseForm({ ...courseForm, imageUrl: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="videoUrl">Video (optional)</Label>
                <div className="space-y-3">
                  {/* Video URL Input */}
                  <div>
                    <Input
                      id="videoUrl"
                      placeholder="https://example.com/course-video.mp4"
                      value={courseForm.videoUrl}
                      onChange={(e) => setCourseForm({ ...courseForm, videoUrl: e.target.value })}
                    />
                  </div>
                  
                  {/* OR Divider */}
                  <div className="flex items-center">
                    <div className="flex-grow border-t border-gray-300"></div>
                    <span className="flex-shrink mx-4 text-gray-400 text-sm">OR</span>
                    <div className="flex-grow border-t border-gray-300"></div>
                  </div>
                  
                  {/* Video File Upload */}
                  <div>
                    <div className="flex items-center gap-3">
                      <Input
                        type="file"
                        accept="video/*"
                        onChange={handleVideoUpload}
                        disabled={uploadingVideo}
                        className="flex-1"
                      />
                      {uploadingVideo && (
                        <div className="flex items-center text-blue-600">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                          Uploading...
                        </div>
                      )}
                    </div>
                    
                    {selectedVideoFile && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                        <div className="flex items-center text-green-800">
                          <Upload className="w-4 h-4 mr-1" />
                          Uploaded: {selectedVideoFile.name} ({(selectedVideoFile.size / 1024 / 1024).toFixed(2)} MB)
                        </div>
                      </div>
                    )}
                    
                    <p className="text-xs text-slate-500 mt-1">{t('adminUI.UploadMpAviMovWmvOrMkvFilesMaximumSizeMb')}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="companyId">{t('adminUI.AssignToCompany')}</Label>
                <Select
                  value={courseForm.companyId || 'global'}
                  onValueChange={(value) => setCourseForm({ ...courseForm, companyId: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select company or leave global" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global (All Companies)</SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name} ({company.companyCode})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Global courses are available to all companies. Company-specific courses are only visible to the selected company.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="trainingType">{t('adminUI.TrainingType')}</Label>
                <Select
                  value={courseForm.trainingType || 'general'}
                  onValueChange={(value) => setCourseForm({ ...courseForm, trainingType: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select training type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General (All Workers)</SelectItem>
                    <SelectItem value="osha_standard">OSHA Standard</SelectItem>
                    <SelectItem value="industry_standard">Industry Standard</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  General courses are available to everyone. OSHA and Industry Standard certifications transfer with workers between companies.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCourseDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateCourse}>
                <Plus className="w-4 h-4 mr-2" />
                Create Course
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manual Create Company Dialog */}
        <Dialog open={showCreateCompanyDialog} onOpenChange={setShowCreateCompanyDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('adminUI.ManuallyAddCompany')}</DialogTitle>
              <DialogDescription>{t('adminUI.CreateACompanyForClientsWhoPayByCheckOrWireTransfer')}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="comp-name">{t('adminUI.CompanyName')}</Label>
                <Input
                  id="comp-name"
                  placeholder="e.g., ABC Construction LLC"
                  value={companyForm.name}
                  onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="comp-code">{t('adminUI.CompanyCode')}</Label>
                <Input
                  id="comp-code"
                  placeholder="e.g., ABC123"
                  value={companyForm.companyCode}
                  onChange={(e) => setCompanyForm({ ...companyForm, companyCode: e.target.value.toUpperCase() })}
                />
                <p className="text-xs text-slate-500">{t('adminUI.UniqueCodeEmployeesWillUseToRegisterWillBeAutoConvertedToUpp')}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">{t('adminUI.ContactName')}</Label>
                  <Input
                    id="contact-name"
                    placeholder="John Smith"
                    value={companyForm.contactName}
                    onChange={(e) => setCompanyForm({ ...companyForm, contactName: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-email">{t('adminUI.ContactEmail')}</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    placeholder="contact@company.com"
                    value={companyForm.contactEmail}
                    onChange={(e) => setCompanyForm({ ...companyForm, contactEmail: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address (optional)</Label>
                <Input
                  id="address"
                  placeholder="123 Main St, City, State ZIP"
                  value={companyForm.address}
                  onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input
                  id="phone"
                  placeholder="(555) 123-4567"
                  value={companyForm.phone}
                  onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="subscription-status">{t('adminUI.SubscriptionStatus')}</Label>
                  <Select
                    value={companyForm.subscriptionStatus}
                    onValueChange={(value) => setCompanyForm({ ...companyForm, subscriptionStatus: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active (Paid)</SelectItem>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={companyForm.isFreeAccount}
                      onChange={(e) => setCompanyForm({ ...companyForm, isFreeAccount: e.target.checked })}
                    />
                    <span>Free Account (No Billing)</span>
                  </Label>
                  <p className="text-xs text-slate-500">{t('adminUI.CheckThisForTestingOrSpecialArrangements')}</p>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateCompanyDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleManualCreateCompany}>
                <Building2 className="w-4 h-4 mr-2" />
                Create Company
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manual Create User Dialog */}
        <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('adminUI.ManuallyAddLearner')}</DialogTitle>
              <DialogDescription>{t('adminUI.CreateAUserLearnerForACompany')}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="user-name">{t('adminUI.FullName')}</Label>
                <Input
                  id="user-name"
                  placeholder="John Smith"
                  value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="user-email">{t('adminUI.EmailAddress')}</Label>
                <Input
                  id="user-email"
                  type="email"
                  placeholder="john@company.com"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="user-password">{t('adminUI.Password')}</Label>
                <Input
                  id="user-password"
                  type="password"
                  placeholder="Create a secure password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="user-role">Role</Label>
                  <Select
                    value={userForm.role}
                    onValueChange={(value) => setUserForm({ ...userForm, role: value, craft: value === 'worker' ? userForm.craft : '' })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="worker">Worker</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                      <SelectItem value="company_admin">Company Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="user-company">{t('adminUI.Company')}</Label>
                  <Select
                    value={userForm.companyId}
                    onValueChange={(value) => setUserForm({ ...userForm, companyId: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name} ({company.companyCode})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Craft Selection - Only for workers */}
              {userForm.role === 'worker' && (
                <div className="space-y-2">
                  <Label htmlFor="user-craft">{t('adminUI.CraftTrade')}</Label>
                  <Select
                    value={userForm.craft}
                    onValueChange={(value) => setUserForm({ ...userForm, craft: value })}
                  >
                    <SelectTrigger id="user-craft">
                      <SelectValue placeholder="Select worker's craft" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {crafts.length === 0 ? (
                        <div className="p-4 text-center text-sm text-slate-500">
                          No crafts available
                        </div>
                      ) : (
                        crafts
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((craft) => (
                            <SelectItem key={craft.id} value={craft.name}>
                              {craft.name}
                            </SelectItem>
                          ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">{t('adminUI.WorkerSTrainingCoursesWillBeCustomizedBasedOnTheirCraft')}</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateUserDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleManualCreateUser}>
                <Users className="w-4 h-4 mr-2" />
                Create Learner
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Company Details Dialog */}
        <Dialog open={showCompanyDialog} onOpenChange={setShowCompanyDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Company Details: {selectedCompany?.name}</DialogTitle>
              <DialogDescription>
                Detailed information for {selectedCompany?.companyCode}
              </DialogDescription>
            </DialogHeader>

            {companyDetails && (
              <div className="space-y-6">
                {/* Company Info */}
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">{t('adminUI.CompanyInformation')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div><strong>Name:</strong> {companyDetails.company.name}</div>
                        <div><strong>Code:</strong> {companyDetails.company.companyCode}</div>
                        <div><strong>Contact:</strong> {companyDetails.company.contactName}</div>
                        <div><strong>Email:</strong> {companyDetails.company.contactEmail}</div>
                        <div><strong>Status:</strong> {companyDetails.company.subscriptionStatus}</div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">{t('adminUI.Statistics')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div><strong>Users:</strong> {companyDetails.users.length}</div>
                        <div><strong>Completions:</strong> {companyDetails.completions.length}</div>
                        <div><strong>Certificates:</strong> {companyDetails.certificates.length}</div>
                        <div><strong>Active Users:</strong> {companyDetails.users.filter(u => u.isActive).length}</div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Union Status Settings */}
                <Card className="border-2 border-blue-200 bg-blue-50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      Training Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-gray-600">
                      All company courses (private and transferable) are visible to assigned workers based on their craft.
                    </div>
                  </CardContent>
                </Card>

                {/* Users Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Users ({companyDetails.users.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>{t('adminUI.Email')}</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>{t('adminUI.Status')}</TableHead>
                          <TableHead>{t('adminUI.Actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {companyDetails.users.slice(0, 10).map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>{user.name}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{user.role}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant={user.isActive ? 'default' : 'secondary'}>
                                {user.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openEditUserDialog(user)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => {
                                    setUserToDelete(user);
                                    setShowDeleteUserDialog(true);
                                  }}
                                >
                                  Delete
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {companyDetails.users.length > 10 && (
                      <p className="text-sm text-slate-500 mt-2">
                        Showing 10 of {companyDetails.users.length} users
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCompanyDialog(false)}>
                Close
              </Button>
              <Button onClick={() => impersonateCompany(selectedCompany?.id)}>
                <UserCog className="w-4 h-4 mr-2" />
                Help This Company
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Company Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Delete Company
              </DialogTitle>
              <DialogDescription>
                This action cannot be undone. This will permanently delete the company and all associated data.
              </DialogDescription>
            </DialogHeader>
            
            {companyToDelete && (
              <div className="py-4 space-y-3">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="font-semibold text-red-900">{companyToDelete.name}</p>
                  <p className="text-sm text-red-700">Code: {companyToDelete.companyCode}</p>
                </div>
                
                <div className="space-y-2 text-sm" data-testid="delete-company-preview">
                  <p className="font-semibold">{t('adminUI.ThisWillDelete')}</p>
                  {deletePreviewLoading && (
                    <p className="text-slate-500">Counting what belongs to this company...</p>
                  )}
                  {deletePreview?.refused && (
                    <p className="text-red-700 font-medium" data-testid="delete-company-refused">
                      {deletePreview.refused}
                    </p>
                  )}
                  {deletePreview && !deletePreview.refused && (
                    <>
                      <ul className="list-disc list-inside space-y-1 text-slate-600 max-h-48 overflow-auto">
                        {Object.entries(deletePreview.collections || {})
                          .sort((a, b) => b[1] - a[1])
                          .map(([collection, count]) => (
                            <li key={collection} data-testid={`delete-count-${collection}`}>
                              {count} {collection.replace(/_/g, ' ')}
                            </li>
                          ))}
                        {deletePreview.gridfsFiles > 0 && (
                          <li>{deletePreview.gridfsFiles} uploaded file(s), photos and signatures</li>
                        )}
                      </ul>
                      <p className="font-medium text-red-900" data-testid="delete-company-total">
                        {deletePreview.totalDocuments} records in total. This cannot be undone.
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowDeleteDialog(false);
                setCompanyToDelete(null);
                setDeletePreview(null);
              }}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteCompany}
                disabled={deleting || deletePreviewLoading || !deletePreview || !!deletePreview?.refused}
                data-testid="confirm-delete-company-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {deleting ? 'Deleting...' : `Delete ${deletePreview?.totalDocuments ?? ''} records`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Edit User Dialog */}
        <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('adminUI.EditUserInformation')}</DialogTitle>
              <DialogDescription>
                Update user details for {editingUser?.name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateUser}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">{t('adminUI.FullName')}</Label>
                    <Input
                      id="edit-name"
                      value={editUserForm.name}
                      onChange={(e) => setEditUserForm({...editUserForm, name: e.target.value})}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-email">{t('adminUI.Email')}</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={editUserForm.email}
                      onChange={(e) => setEditUserForm({...editUserForm, email: e.target.value})}
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-password">New Password (optional)</Label>
                    <Input
                      id="edit-password"
                      type="password"
                      value={editUserForm.password}
                      onChange={(e) => setEditUserForm({...editUserForm, password: e.target.value})}
                      placeholder="Leave blank to keep current"
                    />
                    <p className="text-xs text-slate-500">{t('adminUI.OnlyFillThisIfYouWantToChangeThePassword')}</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-phone">Phone (optional)</Label>
                    <Input
                      id="edit-phone"
                      type="tel"
                      value={editUserForm.phone}
                      onChange={(e) => setEditUserForm({...editUserForm, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-role">Role</Label>
                  <Select
                    value={editUserForm.role}
                    onValueChange={(value) => setEditUserForm({...editUserForm, role: value})}
                  >
                    <SelectTrigger id="edit-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="worker">Worker</SelectItem>
                      <SelectItem value="learner">Learner</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                      <SelectItem value="company_admin">Company Admin</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit-isActive"
                    checked={editUserForm.isActive}
                    onChange={(e) => setEditUserForm({...editUserForm, isActive: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="edit-isActive">{t('adminUI.AccountIsActive')}</Label>
                </div>
              </div>
              
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditUserDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700"
                >{t('adminUI.SaveChanges')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        
        {/* Delete User Confirmation Dialog */}
        <Dialog open={showDeleteUserDialog} onOpenChange={setShowDeleteUserDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('adminUI.DeleteUser')}</DialogTitle>
              <DialogDescription>{t('adminUI.AreYouSureYouWantToDeleteThisUserThisActionCannotBeUndone')}</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
                <p className="font-semibold text-red-900">
                  {userToDelete?.name}
                </p>
                <p className="text-sm text-red-700">
                  Email: {userToDelete?.email}
                </p>
                <p className="text-sm text-red-700">
                  Role: {userToDelete?.role}
                </p>
              </div>
              <div className="mt-4 space-y-2 text-sm text-slate-600">
                <p className="font-semibold text-slate-800">{t('adminUI.ThisWillDelete')}</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>User account and login credentials</li>
                  <li>All course progress and completion records</li>
                  <li>All earned certificates</li>
                  <li>All form submissions</li>
                  <li>All uploaded ID documents</li>
                  <li>All sent and received messages</li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDeleteUserDialog(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteUser}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete User Permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Transfer Worker Dialog */}
        <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-orange-600" />
                Transfer Worker to Another Company
              </DialogTitle>
              <DialogDescription>
                Transfer this worker's account, certificates, and training progress to a different company.
              </DialogDescription>
            </DialogHeader>
            
            {workerToTransfer && (
              <div className="space-y-4 py-4">
                {/* Worker Details */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-2">
                  <h4 className="font-semibold text-slate-900">{t('adminUI.WorkerDetails')}</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-600">{t('adminUI.Name')}</span>
                      <span className="ml-2 font-medium">{workerToTransfer.name}</span>
                    </div>
                    <div>
                      <span className="text-slate-600">{t('adminUI.Email')}</span>
                      <span className="ml-2 font-medium">{workerToTransfer.email}</span>
                    </div>
                    <div>
                      <span className="text-slate-600">{t('adminUI.Role')}</span>
                      <span className="ml-2 font-medium capitalize">{workerToTransfer.role}</span>
                    </div>
                    <div>
                      <span className="text-slate-600">{t('adminUI.CurrentCompany')}</span>
                      <span className="ml-2 font-medium">{workerToTransfer.companyName || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Certificates to Transfer */}
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-2">
                  <h4 className="font-semibold text-green-900 flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    Certificates to Transfer ({workerCertificates.length})
                  </h4>
                  {loadingCertificates ? (
                    <p className="text-sm text-green-700">{t('adminUI.LoadingCertificates')}</p>
                  ) : workerCertificates.length > 0 ? (
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {workerCertificates.map((cert, index) => (
                        <div key={index} className="text-sm text-green-800 flex items-start gap-2 p-2 bg-white rounded border border-green-100">
                          <Award className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="font-medium">{cert.courseName || cert.courseTitle}</div>
                            {cert.expiryDate && (
                              <div className="text-xs text-green-600">
                                Expires: {new Date(cert.expiryDate).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-green-700">{t('adminUI.NoCertificatesToTransfer')}</p>
                  )}
                </div>

                {/* Destination Company Code Input */}
                <div className="space-y-2">
                  <Label htmlFor="destinationCompanyCode">{t('adminUI.DestinationCompanyCode')}</Label>
                  <Input
                    id="destinationCompanyCode"
                    type="text"
                    placeholder="NEWCO123"
                    value={destinationCompanyCode}
                    onChange={(e) => setDestinationCompanyCode(e.target.value.toUpperCase())}
                    onKeyPress={(e) => e.key === 'Enter' && handleTransferWorker()}
                  />
                  <p className="text-xs text-slate-500">{t('adminUI.EnterTheCompanyCodeWhereThisWorkerShouldBeTransferred')}</p>
                </div>

                {/* Transfer Info */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900 font-medium mb-2">{t('adminUI.WhatWillBeTransferred')}</p>
                  <ul className="text-sm text-blue-800 space-y-1 ml-4 list-disc">
                    <li>User account and credentials</li>
                    <li>{workerCertificates.length} certificate{workerCertificates.length !== 1 ? 's' : ''} (expiry dates maintained)</li>
                    <li>All training progress and completion records</li>
                    <li>Receiving company must approve the transfer</li>
                    <li>Worker will receive email notification upon approval</li>
                  </ul>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowTransferDialog(false);
                  setWorkerToTransfer(null);
                  setDestinationCompanyCode('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleTransferWorker}
                disabled={transferring || !destinationCompanyCode.trim()}
              >
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                {transferring ? 'Transferring...' : 'Transfer Worker'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default SuperAdminDashboard;